import { useLocation } from 'react-router-dom'
import { Link } from '@/components/layout/TransitionLink'
import { Info, Menu, Monitor, Moon, ShieldOff, Sun, Users2, WandSparkles } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTheme, type ThemePreference } from '@/hooks/useTheme'

const LINKS = [
  { to: '/', label: 'Overview', icon: null },
  { to: '/instructors', label: 'All instructors', icon: Users2 },
  { to: '/build', label: 'Build a ride', icon: WandSparkles },
  { to: '/unlicensed-artists', label: 'Presumed unavailable', icon: ShieldOff },
  { to: '/about', label: 'About', icon: Info },
]

const THEME_OPTIONS = [
  { value: 'system', label: 'System', icon: Monitor },
  { value: 'light', label: 'Light', icon: Sun },
  { value: 'dark', label: 'Dark', icon: Moon },
] as const

export function NavBar() {
  const location = useLocation()
  const current = LINKS.find((l) => l.to === location.pathname) ?? LINKS[0]
  const { preference, setPreference } = useTheme()
  const PreferenceIcon = THEME_OPTIONS.find((o) => o.value === preference)!.icon

  return (
    <nav className="site-nav sticky top-0 z-40 border-b border-border bg-background/60 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center gap-x-6 px-6 py-3 text-sm">
        {/* Three items still overflow a phone-width row once labels are long
            enough to wrap mid-phrase -- collapse to a menu button below sm
            instead of trying to make the links themselves fit. */}
        <div className="sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Menu className="size-4" />
                {current.label}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {LINKS.map(({ to, label, icon: Icon }) => (
                <DropdownMenuItem key={to} asChild>
                  <Link to={to} className="gap-2">
                    {Icon && <Icon className="size-4" />}
                    {label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="hidden items-center gap-x-6 sm:flex">
          <Link to="/" className="whitespace-nowrap font-medium hover:text-primary">
            Overview
          </Link>
          <Link
            to="/instructors"
            className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground hover:text-foreground"
          >
            <Users2 className="size-4 shrink-0" />
            All instructors
          </Link>
          <Link
            to="/build"
            className="flex items-center gap-1.5 whitespace-nowrap font-medium text-primary hover:text-foreground"
          >
            <WandSparkles className="size-4 shrink-0" />
            Build a ride
          </Link>
          <Link
            to="/unlicensed-artists"
            className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground hover:text-foreground"
          >
            <ShieldOff className="size-4 shrink-0" />
            Presumed unavailable
          </Link>
          <Link
            to="/about"
            className="flex items-center gap-1.5 whitespace-nowrap text-muted-foreground hover:text-foreground"
          >
            <Info className="size-4 shrink-0" />
            About
          </Link>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" className="ml-auto shrink-0" aria-label="Theme">
              <PreferenceIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuRadioGroup value={preference} onValueChange={(v) => setPreference(v as ThemePreference)}>
              {THEME_OPTIONS.map(({ value, label, icon: Icon }) => (
                <DropdownMenuRadioItem key={value} value={value}>
                  <Icon className="size-4" />
                  {label}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </nav>
  )
}
